<div class="mt-6 w-full flex flex-col gap-5">
  <p-toast></p-toast>
  <p-confirmdialog />

  <p-card>
    <ng-template #title>
      <div class="flex items-center justify-between">
        <h1>
          {{
            isEditMode()
              ? ('catalog.products.edit' | translate)
              : ('catalog.products.add' | translate)
          }}
        </h1>
        @if (model()) {
          <span class="text-sm text-color-secondary">
            {{ 'catalog.products.displayPrice' | translate }}:
            <strong>{{ formatDisplayPrice() }}</strong>
          </span>
        }
      </div>
    </ng-template>

    <form
      [formGroup]="form"
      (ngSubmit)="onSubmit()"
      class="flex flex-col gap-5"
    >
      <div class="flex flex-col gap-4" formArrayName="details">
        @for (group of details.controls; track group; let i = $index) {
          <div
            [formGroupName]="i"
            class="flex flex-col gap-3 p-3 rounded border surface-border"
          >
            <div class="flex items-center justify-between">
              <span class="text-sm font-semibold">
                {{ labelForLanguage(group.get('language')?.value) }}
              </span>
              @if (
                group.get('language')?.value !== defaultLanguage
              ) {
                <button
                  pButton
                  type="button"
                  icon="fa fa-trash"
                  text
                  rounded
                  severity="secondary"
                  [attr.aria-label]="'catalog.products.translation.remove' | translate"
                  (click)="removeLanguageRow(i)"
                ></button>
              }
            </div>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div class="flex flex-col">
                <label class="text-sm mb-1">
                  {{ 'catalog.products.translation.titleLabel' | translate }}
                </label>
                <input pInputText formControlName="title" [maxlength]="255" />
                @if (
                  group.get('title')?.touched && group.get('title')?.invalid
                ) {
                  <small class="text-red-500 mt-1">
                    {{ 'validation.required' | translate }}
                  </small>
                }
              </div>
              <div class="flex flex-col">
                <label class="text-sm mb-1">
                  {{ 'catalog.products.translation.shortLabel' | translate }}
                </label>
                <textarea
                  pTextarea
                  formControlName="description_short"
                  rows="2"
                ></textarea>
              </div>
              <div class="flex flex-col md:col-span-2">
                <label class="text-sm mb-1">
                  {{ 'catalog.products.translation.longLabel' | translate }}
                </label>
                <textarea
                  pTextarea
                  formControlName="description_long"
                  rows="3"
                ></textarea>
              </div>
            </div>
          </div>
        }
        <div class="flex items-center gap-2">
          <p-select
            [formControl]="languagePickerControl"
            [options]="availableLanguages()"
            optionLabel="label"
            optionValue="value"
            [placeholder]="'catalog.products.translation.addPlaceholder' | translate"
            [disabled]="availableLanguages().length === 0"
            appendTo="body"
          />
          <p-button
            type="button"
            icon="fa fa-plus"
            [label]="'catalog.products.translation.add' | translate"
            [disabled]="!hasPendingLanguage()"
            (onClick)="addLanguageRow()"
          />
        </div>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div class="flex flex-col">
          <label class="text-sm mb-1">
            {{ 'catalog.products.fields.category' | translate }}
          </label>
          <p-select
            formControlName="category_id"
            [options]="categoryService.categories()"
            optionLabel="title"
            optionValue="id"
            appendTo="body"
          />
        </div>
        <div class="flex flex-col">
          <label class="text-sm mb-1">
            {{ 'catalog.products.fields.openingHourType' | translate }}
          </label>
          <p-select
            formControlName="opening_hour_type_id"
            [options]="openingHourTypeService.items()"
            optionLabel="name"
            optionValue="id"
            appendTo="body"
          />
        </div>

        <div class="flex flex-col">
          <label class="text-sm mb-1">
            {{ 'catalog.products.fields.duration' | translate }}
          </label>
          <p-inputnumber formControlName="duration" [min]="0" suffix=" min" />
        </div>
        <div class="flex flex-col">
          <label class="text-sm mb-1">
            {{ 'catalog.products.fields.interval' | translate }}
          </label>
          <p-inputnumber
            formControlName="interval"
            [min]="30"
            [step]="30"
            [showButtons]="true"
            suffix=" min"
          />
        </div>
        <div class="flex items-center gap-4 mt-5">
          <div class="flex items-center gap-2">
            <p-toggleswitch formControlName="for_request" />
            <label class="text-sm">
              {{ 'catalog.products.fields.forRequest' | translate }}
            </label>
          </div>
          <div class="flex items-center gap-2">
            <p-toggleswitch formControlName="active" />
            <label class="text-sm">
              {{ 'catalog.products.fields.active' | translate }}
            </label>
          </div>
        </div>
      </div>

      <div class="flex flex-col">
        <label class="text-sm mb-1">
          {{ 'catalog.products.fields.notes' | translate }}
        </label>
        <textarea pTextarea formControlName="notes" rows="2"></textarea>
      </div>

      <div class="flex justify-between items-center pt-2 border-t surface-border">
        <p-button
          type="button"
          severity="secondary"
          [label]="'label.cancel' | translate"
          (onClick)="onCancel()"
        />
        <div class="flex flex-col items-end gap-2">
          @if (hasDirtyPanels()) {
            <small class="text-orange-500">
              {{ 'catalog.tickets.dirtyHint' | translate }}
              {{ dirtyPanelTitles().join(', ') }}
            </small>
          }
          <div class="flex gap-2">
            @if (isEditMode() && model()) {
              <p-button
                type="button"
                severity="danger"
                icon="fa fa-trash"
                [label]="'label.delete' | translate"
                (onClick)="onDelete($event)"
              />
            }
            <p-button
              type="submit"
              [label]="'label.save' | translate"
              [loading]="submitting()"
              [disabled]="productSaveDisabled()"
            />
          </div>
        </div>
      </div>
    </form>
  </p-card>

  @if (isEditMode() && model()) {
    <p-card>
      <ng-template #title>
        <h2>{{ 'catalog.prices.historyTitle' | translate }}</h2>
      </ng-template>
      @if (model()?.has_included_tickets) {
        <p-message severity="info" [text]="'catalog.products.priceManagedByIncludedTicket' | translate" />
      } @else {
        <app-price-history
          [owner]="{ type: 'product', id: model()!.id }"
          [prices]="productPrices()"
          (priceCreated)="reload()"
          (priceDeleted)="reload()"
        />
      }
    </p-card>

    <p-card>
      <ng-template #title>
        <div class="flex items-center justify-between">
          <h2>{{ 'catalog.tickets.title' | translate }}</h2>
          <p-button
            size="small"
            icon="fa fa-plus"
            [label]="'catalog.tickets.add' | translate"
            (onClick)="addTicket()"
          />
        </div>
      </ng-template>

      <p class="text-sm text-color-secondary mb-4">
        {{ 'catalog.products.bundleRuleHint' | translate }}
      </p>

      @if (panels().length === 0) {
        <p class="text-sm text-color-secondary italic">
          {{ 'catalog.tickets.empty' | translate }}
        </p>
      } @else {
        <div class="flex flex-col gap-4">
          @for (panel of panels(); track panel.localId) {
            <app-ticket-panel
              [ticket]="panel.ticket"
              [productId]="model()!.id"
              (saved)="onTicketSaved(panel, $event); onPanelDirtyChange()"
              (deleted)="onTicketDeleted(panel)"
            />
          }
        </div>
      }
    </p-card>

    <p-card>
      <ng-template #title>
        <h2>{{ 'catalog.additionalProducts.linkedSectionTitle' | translate }}</h2>
      </ng-template>
      <p class="text-sm text-color-secondary mb-3">
        {{ 'catalog.additionalProducts.linkedSectionHint' | translate }}
      </p>
      <p-multiSelect
        [formControl]="$any(form.get('additional_product_ids'))"
        [options]="additionalProductService.items()"
        optionLabel="title"
        optionValue="id"
        [filter]="true"
        [showClear]="true"
        display="chip"
        appendTo="body"
        styleClass="w-full"
        [placeholder]="'catalog.additionalProducts.linkedPlaceholder' | translate"
      />
    </p-card>
  }
</div>
